import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, ChevronRight, Inbox } from 'lucide-react';
import { useTickets } from '../hooks';
import { TicketStatusBadge, type TicketStatus } from '../components/TicketStatusBadge';
import { ARCHE_PATH } from '../constants';

const STATUS_FILTERS: Array<{ label: string; value: string }> = [
  { label: 'All', value: '' },
  { label: 'Open', value: 'open' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Resolved', value: 'resolved' },
  { label: 'Closed', value: 'closed' },
];

export function MyTicketsPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const { tickets, total, loading, loadMore, hasMore } = useTickets({
    status: statusFilter || undefined,
  });

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Back navigation */}
      <Link to={ARCHE_PATH}>
        <Button variant="ghost" size="sm">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Support
        </Button>
      </Link>

      <div className="space-y-1">
        <h1 className="text-2xl font-bold">My Tickets</h1>
        <p className="text-sm text-muted-foreground">
          {total} {total === 1 ? 'ticket' : 'tickets'} total
        </p>
      </div>

      {/* Status filter tabs */}
      <div className="flex gap-2 flex-wrap">
        {STATUS_FILTERS.map((filter) => (
          <Button
            key={filter.value}
            variant={statusFilter === filter.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter(filter.value)}
          >
            {filter.label}
          </Button>
        ))}
      </div>

      {/* Ticket list */}
      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-lg" />
          ))}
        </div>
      ) : tickets.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Inbox className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">
              {statusFilter
                ? 'No tickets match this filter.'
                : 'You have no support tickets yet. Ask a question on the support page and create a ticket if needed.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {tickets.map((ticket) => (
            <Link
              key={ticket.ticketId}
              to={`${ARCHE_PATH}/tickets/${ticket.ticketId}`}
              className="block"
            >
              <Card className="hover:bg-muted/50 transition-colors">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="space-y-1 flex-1 min-w-0">
                    <p className="font-medium truncate">{ticket.subject}</p>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <TicketStatusBadge status={ticket.status as TicketStatus} />
                      <span>{new Date(ticket.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                </CardContent>
              </Card>
            </Link>
          ))}

          {hasMore && (
            <Button variant="outline" className="w-full" onClick={loadMore}>
              Load more
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
